import React, { useEffect, useRef } from 'react';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { useIntercomUserInfo } from '../hooks/useIntercomUserInfo';
import { useRouter } from 'next/router';
import { useAuthContext } from '@/domains/auth/authContext';
import { usePlatformInfo } from '@/domains/shared/hooks/useIsNative';

// Production:o7xzejfp Staging: ub056ydy
const app_id = process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '';

const ENABLE_INTERCOM_MOBILE =
  process.env.NEXT_PUBLIC_ENABLE_INTERCOM_MOBILE === 'true';

const ENABLE_INTERCOM_WEB =
  process.env.NEXT_PUBLIC_ENABLE_INTERCOM_WEB === 'true';

export const IntercomMessenger: React.FC = () => {
  const { user } = useIntercomUserInfo();
  const { asPath } = useRouter();
  const { isLoggedIn } = useAuthContext();

  const hasInitializedRef = useRef(false);
  const previousJwtRef = useRef<string | null>(null);

  const { isNative } = usePlatformInfo();

  const shouldEnableIntercom =
    typeof window !== 'undefined' &&
    ((ENABLE_INTERCOM_MOBILE && isNative) ||
      (ENABLE_INTERCOM_WEB && !isNative));

  const currentJwt = user?.intercom_user_jwt ?? null;

  // Shut down Intercom when this component unmounts (e.g. pin screen is shown),
  // preventing any interaction while the app is locked. The init effect
  // re-boots it on remount after the user unlocks.
  useEffect(() => {
    if (!shouldEnableIntercom) return;
    return () => {
      shutdown();
    };
  }, [shouldEnableIntercom]);

  useEffect(() => {
    if (!shouldEnableIntercom) return;
    if (!app_id) return;

    const jwtHasChanged = previousJwtRef.current !== currentJwt;

    // First init — boot immediately in visitor mode; JWT will arrive later via a re-run
    if (!hasInitializedRef.current) {
      console.log('[Intercom] initializing');
      Intercom({
        app_id,
        region: 'eu',
        ...(currentJwt ? { intercom_user_jwt: currentJwt } : {}),
      });
      hasInitializedRef.current = true;
      previousJwtRef.current = currentJwt;
      return;
    }

    // Logout — reset to anonymous visitor mode (keep widget visible)
    if (!isLoggedIn && previousJwtRef.current !== null) {
      console.log('[Intercom] logged out; resetting to visitor mode');
      shutdown();
      Intercom({ app_id, region: 'eu' });
      previousJwtRef.current = null;
      return;
    }

    // JWT arrived or rotated — hard-reset to identify the user
    if (jwtHasChanged && currentJwt) {
      console.log('[Intercom] JWT changed; hard resetting');
      shutdown();
      Intercom({ app_id, region: 'eu', intercom_user_jwt: currentJwt });
      previousJwtRef.current = currentJwt;
      return;
    }

    // Route change / normal SPA update
    update({ ...(currentJwt ? { intercom_user_jwt: currentJwt } : {}) });
  }, [shouldEnableIntercom, isLoggedIn, currentJwt, asPath]);

  return null;
};
